import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import { BranchService } from '../../core/services/branch.service';
import { CartService } from '../../core/services/cart.service';
import { ItemService, detailToMenuItem } from '../../core/services/item.service';
import { MenuApiItem, MenuService, MenuTag } from '../../core/services/menu.service';
import { SessionService } from '../../core/services/session.service';
import { StorefrontService } from '../../core/services/storefront.service';
import { UiService } from '../../core/services/ui.service';
import { IconComponent, PriceComponent, SpinnerComponent, TranslatePipe } from '../../shared';

/** One row in the palette list (recommended items or search results). */
interface Row { id: string; name: string; description: string | null; price: number; image: string | null; }

/** Picks a chip icon from the tag's name (tags come from the admin as plain names). */
const TAG_ICONS: [RegExp, string][] = [
  [/best|top|popular|favou?rite/i, 'solar:star-bold'],
  [/spic|hot|chil/i, 'solar:fire-bold'],
  [/new|fresh/i, 'solar:stars-bold'],
  [/plant|vegan|veg|green|salad/i, 'solar:leaf-bold'],
  [/kcal|calor|light|healthy/i, 'solar:heart-pulse-bold'],
  [/under|price|deal|offer|cheap|egp|\d/i, 'solar:tag-price-bold'],
  [/sweet|dessert|cake|cookie|choc|donut/i, 'solar:donut-bitten-bold'],
  [/drink|coffee|tea|shake|juice|bev/i, 'solar:cup-hot-bold'],
  [/chicken|beef|meat|burger|grill/i, 'solar:chef-hat-bold'],
];

/**
 * Desktop search palette: a dialog over the page (opened from the header's search
 * button) with the query box, browse-by-tag chips, recommended items and live
 * results. Esc / backdrop closes; ↑ ↓ + Enter pick a row. Phones keep the /search page.
 */
@Component({
  selector: 'app-search-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent, PriceComponent, SpinnerComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .scrim { position: fixed; inset: 0; z-index: 250; background: rgba(16,12,8,0.5); backdrop-filter: blur(3px);
      display: flex; justify-content: center; align-items: flex-start; padding: 88px 24px 24px; animation: sp-fade .18s ease; }
    .card { width: 100%; max-width: 950px; max-height: calc(100vh - 120px); background: var(--fr-surface); border-radius: 22px;
      box-shadow: 0 30px 80px -20px rgba(0,0,0,0.55); display: flex; flex-direction: column; overflow: hidden;
      animation: sp-pop .2s cubic-bezier(.2,.9,.3,1); }
    .bar { display: flex; align-items: center; gap: 14px; padding: 22px 22px 22px 24px; border-bottom: 1px solid var(--fr-border); }
    .bar input { flex: 1; min-width: 0; border: none; outline: none; background: transparent; font-family: var(--fr-font);
      font-size: 20px; color: var(--fr-text); }
    .bar input::placeholder { color: var(--fr-text-3); }
    .esc { border: 1px solid var(--fr-border-2); background: var(--fr-surface); border-radius: 7px; padding: 4px 9px; cursor: pointer;
      font-family: var(--fr-font); font-size: 12px; font-weight: 700; color: var(--fr-text-3); }
    .clr { border: none; background: none; cursor: pointer; color: var(--fr-text-3); padding: 2px; display: flex; }
    .body { overflow-y: auto; padding: 8px 22px 22px; }
    .lbl { font-size: 12.5px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--fr-text-3);
      margin: 18px 0 12px; display: flex; align-items: center; justify-content: space-between; }
    .lbl button { border: none; background: none; cursor: pointer; font-family: var(--fr-font); font-size: 12.5px; font-weight: 700;
      color: var(--brand-deep); letter-spacing: 0; text-transform: none; }
    .chips { display: flex; flex-wrap: wrap; gap: 10px; }
    .chip { display: inline-flex; align-items: center; gap: 8px; height: 42px; padding: 0 17px; border-radius: 999px; cursor: pointer;
      border: 1.5px solid var(--fr-border-2); background: var(--fr-surface); font-family: var(--fr-font); font-size: 15px;
      font-weight: 700; color: var(--fr-text); transition: border-color .15s ease, background .15s ease; }
    .chip:hover { border-color: color-mix(in srgb, var(--brand) 45%, var(--fr-border-2)); }
    .chip.on { border-color: var(--brand); background: var(--brand-tint); color: var(--brand-deep); }
    .rows { display: grid; grid-template-columns: repeat(auto-fill, minmax(400px, 1fr)); gap: 4px 18px; }
    .row { display: flex; align-items: center; gap: 15px; padding: 10px 12px; border-radius: 14px; cursor: pointer; border: none;
      background: transparent; text-align: start; font-family: var(--fr-font); width: 100%; }
    .row:hover, .row.hl { background: var(--fr-surface-2, var(--fr-surface-3)); }
    .thumb { position: relative; width: 66px; height: 66px; border-radius: 12px; overflow: hidden; flex-shrink: 0; background: var(--fr-surface-3); }
    .thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .qty { position: absolute; top: 4px; inset-inline-end: 4px; min-width: 20px; height: 20px; padding: 0 5px; border-radius: 10px;
      background: var(--brand); color: #fff; font-size: 11px; font-weight: 750; display: flex; align-items: center; justify-content: center; }
    .nm { font-weight: 750; font-size: 16px; color: var(--fr-text); letter-spacing: -0.01em; }
    .ds { font-size: 13.5px; color: var(--fr-text-3); margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .empty { padding: 34px 10px; text-align: center; color: var(--fr-text-2); font-size: 14px; }
    .soon { display: flex; align-items: center; gap: 12px; margin-top: 22px; padding: 14px 18px; border-radius: 14px;
      background: color-mix(in srgb, var(--fr-info) 8%, var(--fr-surface)); border: 1px solid color-mix(in srgb, var(--fr-info) 22%, transparent);
      font-size: 14.5px; color: var(--fr-text-2); }
    .soon b { color: var(--fr-text); font-weight: 750; }
    @keyframes sp-fade { from { opacity: 0; } }
    @keyframes sp-pop { from { opacity: 0; transform: translateY(-10px) scale(.985); } }
  `],
  template: `<div *ngIf="open" class="scrim" (click)="close()">
    <div class="card" role="dialog" aria-modal="true" [attr.aria-label]="'Search the menu' | t" (click)="$event.stopPropagation()">
      <div class="bar">
        <app-icon name="solar:magnifer-linear" [size]="24" color="var(--fr-text-3)"></app-icon>
        <input #box [ngModel]="q" (ngModelChange)="onQ($event)" [placeholder]="'Search dishes, drinks, desserts…' | t"
          maxlength="100" autocomplete="off" (keydown)="onKey($event)">
        <app-spinner *ngIf="searching" [size]="17" color="var(--fr-text-3)"></app-spinner>
        <button *ngIf="q && !searching" class="clr" (click)="clear()" [attr.aria-label]="'Clear' | t">
          <app-icon name="solar:close-circle-bold" [size]="20"></app-icon>
        </button>
        <button class="esc" (click)="close()">Esc</button>
      </div>

      <div class="body">
        <!-- browse by tag (multi-select) -->
        <ng-container *ngIf="!q.trim() && tags.length">
          <div class="lbl">{{ 'Search by tag' | t }}
            <button *ngIf="activeTagIds.length" (click)="clearTags()">{{ 'Clear' | t }}</button>
          </div>
          <div class="chips">
            <button *ngFor="let tg of tags" class="chip" [class.on]="isTagOn(tg.id)" (click)="toggleTag(tg)">
              <app-icon [name]="isTagOn(tg.id) ? 'solar:check-circle-bold' : tagIcon(tg.name)" [size]="17" color="var(--brand-deep)"></app-icon>
              {{ tg.name }}
            </button>
          </div>
        </ng-container>

        <!-- results (typed query or active tags) -->
        <ng-container *ngIf="filtering; else recommended">
          <div class="lbl">{{ 'Results' | t }}</div>
          <div *ngIf="!searching && !results.length" class="empty">
            {{ q.trim() ? ('No results for' | t) + ' “' + q.trim() + '”' : ('No results' | t) }}
          </div>
          <div class="rows">
            <ng-container *ngTemplateOutlet="rowsTpl; context: { $implicit: results }"></ng-container>
          </div>
        </ng-container>

        <ng-template #recommended>
          <ng-container *ngIf="recommendedRows.length">
            <div class="lbl">{{ (recentMode ? 'Recently ordered' : 'Recommended for you') | t }}</div>
            <div class="rows">
              <ng-container *ngTemplateOutlet="rowsTpl; context: { $implicit: recommendedRows }"></ng-container>
            </div>
          </ng-container>
        </ng-template>

        <div class="soon">
          <app-icon name="solar:magic-stick-3-bold" [size]="22" color="var(--fr-info)"></app-icon>
          <span><b>{{ 'Natural search — coming soon.' | t }}</b> {{ 'Type things like “something spicy under 150” or “no onions”.' | t }}</span>
        </div>
      </div>
    </div>
  </div>

  <ng-template #rowsTpl let-list>
    <button *ngFor="let it of list; let i = index" class="row" [class.hl]="i === hl" (mouseenter)="hl = i" (click)="pick(it)">
      <span class="thumb">
        <img [src]="it.image || logo" alt="" onerror="this.style.visibility='hidden'">
        <span *ngIf="cartQty(it.id) > 0" class="qty fr-num">{{ cartQty(it.id) }}</span>
      </span>
      <span style="flex:1;min-width:0">
        <span class="nm" style="display:block">{{ it.name }}</span>
        <span *ngIf="it.description" class="ds" style="display:block">{{ it.description }}</span>
      </span>
      <app-price [value]="it.price" [size]="16" [weight]="750"></app-price>
    </button>
  </ng-template>`,
})
export class SearchOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('box') box?: ElementRef<HTMLInputElement>;

  open = false;
  q = '';
  searching = false;
  results: Row[] = [];
  tags: MenuTag[] = [];
  activeTagIds: string[] = [];
  /** Recommended rows: the customer's recent items, else best sellers, else the menu's first items. */
  recommendedRows: Row[] = [];
  recentMode = false;
  /** Keyboard-highlighted row in the visible list. */
  hl = -1;

  private readonly q$ = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(
    private ui: UiService,
    private menuSvc: MenuService,
    private itemSvc: ItemService,
    private branchSvc: BranchService,
    private sf: StorefrontService,
    private cart: CartService,
    private session: SessionService,
    private cdr: ChangeDetectorRef,
  ) {}

  /** Stand-in for items with no photo: the default item image, else the logo. */
  get logo(): string { return this.sf.brand?.itemImage || this.sf.brand?.logo || ''; }
  get filtering(): boolean { return !!this.q.trim() || this.activeTagIds.length > 0; }
  private get visible(): Row[] { return this.filtering ? this.results : this.recommendedRows; }

  ngOnInit(): void {
    this.subs.push(this.ui.searchOpen$.subscribe((o) => (o ? this.show() : this.hide())));
    this.subs.push(this.q$.pipe(
      map((v) => v.trim()),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((t) => {
        const branchId = this.branchSvc.currentId;
        if (!t || !branchId) { this.searching = false; return of([] as MenuApiItem[]); }
        this.searching = true; this.cdr.markForCheck();
        return this.menuSvc.search(branchId, t).pipe(catchError(() => of([] as MenuApiItem[])));
      }),
    ).subscribe((r) => { this.results = r.map(toRow); this.searching = false; this.hl = r.length ? 0 : -1; this.cdr.markForCheck(); }));
  }
  ngOnDestroy(): void { this.subs.forEach((s) => s.unsubscribe()); }

  private show(): void {
    this.open = true;
    this.q = ''; this.results = []; this.activeTagIds = []; this.hl = -1; this.q$.next('');
    this.loadTags();
    this.loadRecommended();
    this.cdr.markForCheck();
    setTimeout(() => this.box?.nativeElement.focus(), 30);
  }
  private hide(): void { this.open = false; this.cdr.markForCheck(); }
  close(): void { this.ui.closeSearch(); }

  @HostListener('document:keydown.escape')
  onEsc(): void { if (this.open) this.close(); }

  private loadTags(): void {
    if (this.tags.length) return;
    this.menuSvc.tags('search').subscribe({ next: (t) => { this.tags = t; this.cdr.markForCheck(); }, error: () => {} });
  }

  private loadRecommended(): void {
    const branchId = this.branchSvc.currentId;
    const take = (rows: Row[], recent = false) => { this.recommendedRows = rows.slice(0, 6); this.recentMode = recent; this.cdr.markForCheck(); };
    const bestOrMenu = () => {
      const best = this.sf.collectionByType('best_sellers');
      if (best?.itemsUrl) {
        this.sf.collectionItems(best.itemsUrl).subscribe((items) => (items.length ? take(items) : menuFirst()));
      } else menuFirst();
    };
    const menuFirst = () => {
      if (!branchId) return;
      this.menuSvc.getMenu(branchId).subscribe({
        next: (m) => take(m.sections.flatMap((s) => s.items).map(toRow)),
        error: () => {},
      });
    };
    if (branchId && this.session.loggedIn) {
      this.menuSvc.recentItems(branchId).subscribe({
        next: (r) => (r.length ? take(r.map(toRow), true) : bestOrMenu()),
        error: () => bestOrMenu(),
      });
    } else bestOrMenu();
  }

  onQ(v: string): void {
    this.q = v;
    if (this.activeTagIds.length) this.activeTagIds = []; // typing exits tag mode
    this.q$.next(v);
  }
  clear(): void { this.q = ''; this.results = []; this.hl = -1; this.q$.next(''); this.box?.nativeElement.focus(); }

  // ── tags ──
  isTagOn(id: string): boolean { return this.activeTagIds.includes(id); }
  tagIcon(name: string): string { return TAG_ICONS.find(([re]) => re.test(name))?.[1] ?? 'solar:tag-bold'; }
  toggleTag(tag: MenuTag): void {
    const branchId = this.branchSvc.currentId;
    if (!branchId) return;
    this.q = '';
    this.activeTagIds = this.isTagOn(tag.id) ? this.activeTagIds.filter((id) => id !== tag.id) : [...this.activeTagIds, tag.id];
    if (!this.activeTagIds.length) { this.results = []; this.cdr.markForCheck(); return; }
    this.searching = true; this.results = []; this.cdr.markForCheck();
    this.menuSvc.search(branchId, '', this.activeTagIds).pipe(catchError(() => of([] as MenuApiItem[])))
      .subscribe((r) => { this.results = r.map(toRow); this.searching = false; this.hl = r.length ? 0 : -1; this.cdr.markForCheck(); });
  }
  clearTags(): void { this.activeTagIds = []; this.results = []; this.cdr.markForCheck(); }

  // ── keyboard: ↑ ↓ move, Enter opens ──
  onKey(e: KeyboardEvent): void {
    const n = this.visible.length;
    if (!n) return;
    if (e.key === 'ArrowDown') { this.hl = (this.hl + 1) % n; e.preventDefault(); }
    else if (e.key === 'ArrowUp') { this.hl = (this.hl - 1 + n) % n; e.preventDefault(); }
    else if (e.key === 'Enter') { this.pick(this.visible[Math.max(0, this.hl)]); e.preventDefault(); }
  }

  cartQty(id: string): number { return this.cart.qtyOf(id); }

  /** Close the palette and open the item's options sheet. */
  pick(it: Row): void {
    const branchId = this.branchSvc.currentId;
    if (!it || !branchId) return;
    this.close();
    this.ui.beginItemLoad();
    this.itemSvc.getItem(it.id, branchId).subscribe({
      next: (detail) => this.ui.openItem(detailToMenuItem(detail, this.logo)),
      error: () => { this.ui.closeItem(); this.ui.showToast('Could not load this item'); },
    });
  }
}

function toRow(it: MenuApiItem): Row {
  return { id: it.id, name: it.name, description: it.description, price: it.price, image: it.image };
}
